import React, { useState } from "react";
import {
  SortBy,
  Filters as ReactifyFilters,
  FiltersSelected,
  ClearAll,
  useResults,
  useSearch,
} from "@usereactify/search";

import { useAppContext } from "../../../context/AppContext";
import { FilterList } from "./filters-list";
import { ClearAll as MainClearAll } from "./clear-all";
import Icons from "../Icons/icons";
import { overlayStyle, drawerStyle } from "./styles";

const Filters = ({ searchPage = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { resultStats } = useResults();
  const { searchTerm, setSearchTerm, submitSearchTerm } = useSearch();

  const handleToggle = React.useCallback(() => {
    setIsOpen(!isOpen);
  }, [isOpen]);

  const handleSearch = React.useCallback(
    (e) => {
      e.preventDefault();
      submitSearchTerm(searchTerm);
    },
    [submitSearchTerm, searchTerm]
  );

  const submitSearchKey = React.useCallback(
    (e) => {
      if (e.key === "Enter") {
        handleSearch(e);
      }
    },
    [handleSearch]
  );

  React.useEffect(() => {
    if (resultStats?.numberOfResults <= 0) {
      document.body.classList.add("rs-no-results");
    } else {
      document.body.classList.remove("rs-no-results");
    }

    if (isOpen) {
      document.body.classList.add("rs-filters-open");
    } else {
      document.body.classList.remove("rs-filters-open");
    }

    if (searchPage) {
      const title = document.querySelector("title");
      if (title) {
        title.innerHTML = `Search: ${resultStats?.numberOfResults} results found for "${searchTerm}" – bassike`;
      }
    }
    // fixes slider issue
    window.dispatchEvent(new Event("resize"));
  }, [resultStats?.numberOfResults, searchTerm, isOpen]);

  return (
    <div className={`rs-filters-wrapper ${searchPage && "search-page section-spacing-padding-x"}`}>
      {searchPage && (
        <>
          <div className={`search-label`}>
            <p className="text-md">Showing {resultStats?.numberOfResults} search results</p>
          </div>
          <div className="search-filter-wrapper">
            <form action="/search">
              <fieldset>
                <input
                  type="search"
                  className="search-field"
                  placeholder="Search for anything"
                  value={searchTerm}
                  name="q"
                  onChange={(event) => setSearchTerm(event.target.value)}
                  onKeyDown={(e) => submitSearchKey(e)}
                />
                <button onClick={handleSearch} type="submit" className="thb-search-submit">
                  <Icons name="search" />
                </button>
              </fieldset>
              <input type="hidden" enterKeyHint="search" />
            </form>

            {resultStats?.numberOfResults <= 0 && (
              <div className="text-sm">
                <p className="m-0 py-24">
                  No results for '{searchTerm}'. Check your spelling, try another search or browse our popular
                  collections below
                </p>
              </div>
            )}
          </div>
        </>
      )}
      <div className="filter-drawer-outter">
        <div className={`filter-drawer-wrapper facets--bar ${!searchPage && "section-spacing-padding-r"}`}>
          <button onClick={handleToggle} className="facets-toggle text-sm">
            {" "}
            <Icons name="filter" /> Filter and Sort
          </button>
          <CollectionViewToggler />
          <div className="drawer-overlay" style={overlayStyle(isOpen)} onClick={handleToggle} />
          <div className="drawer-inner" style={drawerStyle(isOpen)}>
            <div className="drawer-inner-header section-spacing-padding-y section-spacing-padding-x">
              <h3 className="body-font h3">add filters</h3>
              <button className="close-btn" onClick={handleToggle}>
                <Icons name="close" />
              </button>
            </div>
            <div className="drawer-inner-content">
              <div className="clear-all-wrapper section-spacing-padding-x">
                <FiltersSelected />
                <div className="clear-all">
                  <ClearAll showWhenEmpty={false} render={MainClearAll} />
                </div>
              </div>

              <div className=" collection-view-toggler-wrapper section-spacing-padding-y section-spacing-padding-x">
                <CollectionViewToggler viewLabel="Images" />
                <div className="thb-filter-sort">
                  <div className="thb-filter-title">Sort by</div>
                  <SortBy />
                </div>
                <ReactifyFilters renderFilterList={(props) => <FilterList {...props} />} />
              </div>
            </div>
            <div className="drawer-inner-footer">
              <div className="button solid-accent btn-md">
                <a className="mobile-filters-clear text-button before-none after-none">
                  <ClearAll showWhenEmpty={true} render={(props) => <MainClearAll {...props} text="clear" />} />
                </a>
              </div>
              <button className="button mobile-filters-apply solid-black btn-md" onClick={handleToggle}>
                <span>view {resultStats?.numberOfResults} results</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Filters;

// viewToggler
const CollectionViewToggler = ({ viewLabel = "View:" }) => {
  const { activeView, views, setActiveView } = useAppContext();

  const handleViewClick = React.useCallback(
    (viewId) => {
      setActiveView(viewId);
      window.dispatchEvent(new Event("resize"));
    },
    [setActiveView]
  );

  return (
    <div className="plp-view-toggler flex-container gap-8 text-sm">
      <div className="plp-view-toggler__label">{viewLabel}</div>
      <div className="plp-view-toggler__buttons flex-container gap-8 ghost-view-wrapper">
        {views.map((view) => (
          <button
            key={view.id}
            className={`plp-view-toggler__button ${view.id === activeView ? "active" : ""}`}
            onClick={() => handleViewClick(view.id)}
            data-view={view.id}
          >
            {view.label}
          </button>
        ))}
      </div>
    </div>
  );
};

